import React, { useState } from "react"
import { RichText as PrismicRichText } from "prismic-reactjs"
import camelCase from "lodash/camelCase"

import CallToAction from "legacy/components/shared/CallToAction"
import Column from "./Column"
import { Element, Text, TextProps } from "legacy/components/shared/base"
import Grid from "legacy/components/shared/Grid"
import { Maybe } from "types/Maybe"
import PageControls from "./PageControls"
import RichText from "legacy/components/shared/RichText"
import { ThreeColumnSectionType } from "types/shared-components"
import { mapFontSize } from "src/legacy/utils"

interface OwnProps {
  data: Maybe<ThreeColumnSectionType>
}

export type ThreeColumnSectionProps = OwnProps

const ThreeColumnSection: React.FC<ThreeColumnSectionProps> = ({
  data,
}): React.ReactElement | null => {
  if (!data?.primary || !data?.items) return null
  const [page, setPage] = useState(1)

  const { primary, items } = data
  const {
    background_color,
    button_text,
    button_type,
    button_url,
    button_background_color,
    button_text_color,
    center_column_content,
    headline_font_size,
    headline_text_alignment,
    headline_text_alignment_mobile,
    paginate_items,
    removed_vertical_padding,
    wysiwyg,
  } = primary
  const bgColor = background_color ? camelCase(background_color) : "white"
  const headlineMaxWidth =
    headline_text_alignment === "left"
      ? "contentWidth"
      : ["350px", "contentWidth"]
  const headlineText = primary.headline?.richText
  const headlineTextAlignment = (headline_text_alignment ||
    "center") as TextProps["textAlign"]
  const headlineMobileMargin =
    headline_text_alignment_mobile === "center" ? "0 auto 30px" : "0 0 30px"
  const headlineMargin = [headlineMobileMargin, "0 auto 75px", "0 auto 95px"]
  const headlineFontSize = headline_font_size
    ? mapFontSize("header", headline_font_size)
    : ["28", "text.header"]
  const headlineTextAlignmentMobile = headline_text_alignment_mobile || "left"
  const wysiwygText = wysiwyg?.richText
  const hasWysiwyg = wysiwygText && wysiwygText[0]?.text

  const bottomPadding =
    removed_vertical_padding === "both" || removed_vertical_padding === "bottom"
      ? "0"
      : ["40", "60", "90"]
  const topPadding =
    removed_vertical_padding === "both" || removed_vertical_padding === "top"
      ? "0"
      : ["40", "60", "90"]

  const amountPerPage = 6
  const isPaginated = paginate_items && data.items.length > amountPerPage
  const reversedItems = [...items].reverse()

  let threeColItems = []

  if (isPaginated) {
    const paginatedItems = reversedItems.filter((item, index) => {
      if (
        index < amountPerPage * page &&
        index >= amountPerPage * page - amountPerPage
      ) {
        return item
      }
    })
    threeColItems = [...paginatedItems]
  } else {
    threeColItems = [...items]
  }

  return (
    <Element bg={bgColor}>
      <Element
        m="0 auto"
        maxWidth="maxWidth"
        px="40"
        pb={bottomPadding}
        pt={topPadding}
      >
        {headlineText.length > 0 ? (
          <Text
            as="h2"
            fontSize={headlineFontSize}
            lineHeight={["34px", "1.4"]}
            m={headlineMargin}
            maxWidth={headlineMaxWidth}
            textAlign={[headlineTextAlignmentMobile, headlineTextAlignment]}
            variant="header"
          >
            {PrismicRichText.asText(headlineText)}
          </Text>
        ) : null}
        <Grid gridTemplateColumns={["1col", "2col", "2col", "3col"]}>
          {threeColItems.map((item, index) => {
            return (
              <Column
                alignment={center_column_content ? "center" : "left"}
                data={item}
                index={index}
                key={`column_${index}`}
                totalItems={threeColItems.length}
              />
            )
          })}
        </Grid>
        {isPaginated && (
          <PageControls
            amountPerPage={amountPerPage}
            page={page}
            setPage={setPage}
            totalItems={items.length}
          />
        )}
        {button_text && button_url && (
          <CallToAction
            bg={button_background_color}
            color={button_text_color}
            linkType={button_url.link_type}
            mt={["50", "0", "10"]}
            text={button_text}
            textAlign="center"
            type={button_type}
            url={button_url.url}
          />
        )}
        {hasWysiwyg ? (
          <RichText
            m="0 auto"
            maxWidth="contentWidth"
            pt={["40", "60", "90"]}
            render={wysiwygText}
          />
        ) : null}
      </Element>
    </Element>
  )
}

export default ThreeColumnSection
